import { i18n } from '@/commons/utils';
import { removeStoreCookie } from '../commons/header/header-store-locator';

/**
 * Get all input value from a form or from a specific part of the form.
 *
 * @param {HTMLFormElement} form
 * @param {String} selector The form part selector.
 * @return {Object} An Object of parameters.
 */
export const getParameters = (form, selector) => {
  const data = {};
  let fields = form.elements && [...form.elements];

  if (selector) fields = fields.filter(field => field.closest(selector));

  fields.forEach(field => {
    if (
      field.name &&
      ((field.type === 'select-one' && (!field.classList.contains('selectpicker') || field.parentNode.offsetParent)) ||
        ((field.type === 'checkbox' || field.type === 'radio') && field.checked) ||
        (field.type === 'hidden' && field.name !== 'email') ||
        field.offsetParent)
    )
      data[field.name] = field.value;
  });

  return data;
};

/**
 * Get all parameters from an URL.
 *
 * @param {String} querystring querystring part of the URL.
 * @return {Object} An Object of parameters.
 */
export const getURLParameters = querystring => {
  const parameters = {};

  if (!querystring) return null;

  querystring
    .split('?')[1]
    .split('#')[0]
    .split('&')
    .forEach(string => {
      const [key, value] = string.split('=');

      parameters[key] = decodeURIComponent(value);
    });

  return parameters;
};

/**
 * Get all hash parameters from an URL.
 *
 * @param {String} hash Hash of the URL.
 * @return {Object} An Object of parameters.
 */
export const getHashParameters = hash => {
  const parameters = {};

  if (!hash) return null;

  hash
    .split('#')[1]
    .split('&')
    .forEach(string => {
      const [key, value] = string.split('=');

      parameters[key] = decodeURIComponent(value);
    });

  return parameters;
};

/**
 * Get a specific parameter from an URL.
 *
 * @param {String} querystring querystring part of a URL.
 * @return {Object} An Object of parameters.
 */
export const getURLParameter = (querystring, parameter) => {
  const parameters = getURLParameters(querystring);

  return parameters && parameters[parameter];
};

/**
 * Discard unsaved changes.
 *
 * @param {HTMLElement} form The form to reset.
 */
export const resetForm = form => {
  const formGoup = form.$$('.form-group');

  formGoup.forEach(element => {
    const { classList } = element;

    classList.remove('has-error');
    classList.remove('validated');
  });

  form.reset();
  form.$$('.selectpicker').forEach(selectpicker => {
    selectpicker.dispatchEvent(new CustomEvent('change'));
    $(selectpicker).selectpicker('refresh');
  });
};

/**
 * Truncate customer's name when it is too long and add dots.
 *
 * @param {HTMLElement} container customer's name container.
 * @param {Boolean} init "true" if it is the first time we call this function.
 */
export const ellipsify = (container, init = true) => {
  let text = container.textContent;

  if (init) {
    text = text.trim();
    container.innerHTML = container.innerHTML.replace(/-/g, '<span>-</span>');
  }

  if (container.scrollHeight - container.offsetHeight > 0 || container.scrollWidth - container.offsetWidth > 0) {
    container.textContent = `${text.slice(0, -6)}...`;
    ellipsify(container, false);
  }
};

/**
 * Disconnect current user and clean storage.
 *
 * @param {HTMLElement} button the disconnect button.
 */
export function logout(e) {
  e.preventDefault();

  const { logoutUrl, cpUrl } = this.dataset;

  cube.storage.remove('giftcardFormData'); // (remove giftcard form info in local Storage)
  removeStoreCookie();
  if (window.cube.config.anly_useLegacy === 'Y') {
    CUBE.Personalization.forceSendPersonalization();
  }

  const urls = cpUrl && JSON.parse(cpUrl);

  if (urls && urls.length)
    urls.forEach(url =>
      $.getScript(url, () => {
        window.location.href = logoutUrl;
      }).fail(() => {
        window.location.href = logoutUrl;
      })
    );
  else window.location.href = logoutUrl;
}

/**
 * Add validation rules to all the fields of a form.
 *
 * @param {HTMLFormElement} form The form that will receive the rules.
 * @param {String} ignore
 * @param  {...Object} rules Several rules objects.
 */
export const validator = (form, ignore = ':hidden', ...rules) => {
  const mergedRules = mergeObjects(rules);

  $(form).validate(
    Object.assign($.validator.cubeOptions, mergedRules, {
      ignore,

      unhighlight: (element, errorClass, validClass) => {
        const parent = element.closest('.form-group');

        if (!parent) return;

        const { classList } = parent;

        if (['radio', 'checkbox'].includes(element.type))
          parent.$$(`.${element.type}`).forEach(input => {
            input.classList.remove(errorClass);
          });
        else classList.remove(errorClass);

        if (element.value.length && validClass) classList.add(validClass);
        else if (validClass) classList.remove(validClass);
      },
    })
  );
};

/**
 * Make a deep merge of two or more objects.
 *
 * @param {Array<Object>} objects An array of objects.
 * @return {Object}
 */
const mergeObjects = objects => {
  const source = objects.shift();

  objects.forEach(object => {
    Object.keys(object).forEach(key => {
      const value = object[key];
      const sourceValue = source[key];

      if (sourceValue && typeof value === 'object') {
        if (value.length) value.forEach(element => !sourceValue.includes(element) && sourceValue.push(element));
        else mergeObjects([sourceValue, value]);
      } else {
        source[key] = value;
      }
    });
  });

  return source;
};

/**
 * Get some customer data in session storage.
 *
 * @param {String} type 'MAIN' or 'SECOND' identifier.
 */
export const getStoredIdentifier = (type = 'MAIN') => {
  const types = { MAIN: 'mainIdentifier', SECOND: 'secondIdentifier' };

  try {
    return JSON.parse(sessionStorage.getItem(types[type])) || {};
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Save some customer data in session storage.
 *
 * @param {Object} data An object of customer data.
 * @param {String} type 'MAIN' or 'SECOND' identifier.
 */
export const setStoredIdentifier = (data, type) => {
  if (type === 'MAIN') sessionStorage.setItem('mainIdentifier', JSON.stringify(data));

  if (type === 'SECOND') sessionStorage.setItem('secondIdentifier', JSON.stringify(data));
};

/**
 * Remove customer data in session storage.
 *
 */
export const removeStoredIdentifier = () => {
  sessionStorage.removeItem('mainIdentifier');
  sessionStorage.removeItem('secondIdentifier');
};

/**
 * Enable or disable the company checkbox
 *
 * @param {Boolean} disabled
 */
export const disableCompanyCheckbox = (disabled = true) => {
  const btobcheckbox = cube.$id('js-btob-checkbox');

  if (!btobcheckbox) return;

  const formGroup = btobcheckbox.closest('.form-group');

  btobcheckbox.disabled = disabled;
  formGroup.classList.toggle('account-form__field--disabled', disabled);
};

/**
 * Set the customer person id in a specific DOM field ( continue without login process ).
 *
 * @param {Integer} id The customer person id.
 */
export const setSmartId = id => {
  const smartId = cube.$id('js-smart-id');

  smartId.value = id;
};

/**
 * Set the customer member type in a specific DOM field.
 *
 * @param {Integer} id The customer member type.
 */
export const setMemberType = type => {
  const memberType = cube.$id('member-type');

  memberType.value = type;
};

/**
 * Checked or unchecked the company checkbox Programmatically.
 *
 * @param {Integer} personType
 */
export const setCustomerType = personType => {
  const btobcheckbox = cube.$id('js-btob-checkbox');

  if (!btobcheckbox) return;

  if (personType === 2) btobcheckbox.checked = true;
  else btobcheckbox.checked = false;

  btobcheckbox.dispatchEvent(new CustomEvent('change'));
};

/**
 * Set the customer person id in a specific DOM field.
 *
 * @param {Integer} id The customer person id.
 */
export const setPersonId = id => {
  const personId = cube.$id('personId');

  personId.value = id;
};

/**
 * Display an error if the XHR returns error(s).
 *
 * @param {Json} response The XHR response.
 * @param {HTMLElement} The element to enable.
 * @returns {Boolean}
 */
export const ajaxError = (response, element) => {
  if (
    typeof response === 'string' ||
    (typeof response === 'object' &&
      response.responseTO &&
      response.responseTO.errors[0] &&
      response.responseTO.errors[0].message)
  ) {
    if (typeof response === 'string') cube.alert.error(i18n('error.oc.call'));
    else cube.alert.error(response.responseTO.errors[0].message);

    if (element) element.disabled = false;

    return true;
  }

  return false;
};

/**
 * Display a list of options if the customer's identifier already exists.
 *
 * @param {Boolean} displayed Display or hide the option list.
 * @param {String} identiferType 'MAIN' or 'SECOND' identifier.
 * @param {Array<String>} options The list of the options to display.
 */
export const displayOptions = (displayed = true, identifierType = 'MAIN', options = []) => {
  const optionList =
    identifierType === 'MAIN' ? cube.$id('js-login-option-block') : cube.$id('js-second-identifier-option-block');

  if (!optionList) return;

  const items = optionList.$$('.account-tooltip__content__option');

  items.forEach(item => {
    if (options.includes(item.dataset.id)) item.classList.remove('hidden');
    else item.classList.add('hidden');
  });

  if (displayed) optionList.classList.remove('hidden');
  else optionList.classList.add('hidden');
};

/**
 * Disable or enable the form submit button.
 *
 * @param {Boolean} enabled Enable or disable the submit button.
 */
export const disableSubmit = (disabled = true, formType) => {
  const button = formType === 'CREATE' ? cube.$id('js-create-account-button') : cube.$id('js-complete-account-button');
  button.disabled = disabled;
};



// WEBPACK FOOTER //
// ./src/customer/customer-utils.js